import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    ERequestStatus,
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import PopupCallBack from '@r1-frontend/ui-react/components/popups/popupCallback';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { CATEGORIES } from '@r1-frontend/shared/constants/analytics';
import { isClientSide } from '@r1-frontend/shared/helpers/ssr';

import PopupResult from '~/src/components/Popups/PopupResult';
import { ISubmitParams } from '~/src/components/RequestFormPhone';
import TariffCard from '~/src/components/TariffCard';
import { TariffLineCarousel } from '~/src/components/TariffLineCarousel';
import { ITariff } from '~/src/entities/formattedTariff';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from './styled';

export enum ESortingKeys {
    WEIGHT = 'weight',
    INTERNET = 'sortOnInternet',
    INTERNET_LINES = 'sortOnInternetLines',
    BUNDLES = 'sortOnBundles',
}
export interface ITariffLine {
    id: number,
    title: string,
    body: string,
    sort: number,
    tariffs: ITariff[],
}

interface ITariffLineProps {
    isAuth?: boolean,
    line?: ITariffLine,
    baseTariffs?: ITariff[],
    sortField?: string,
    openPopup?: (tariff: ITariff) => void,
    className?: string,
    weight?: number | null,
}

const TariffLine = ({
    line,
    isAuth = false,
    baseTariffs = [],
    sortField = ESortingKeys.WEIGHT,
    openPopup = () => ({}),
    className = '',
}: ITariffLineProps): JSX.Element => {
    const [onClientSide, setOnClientSide] = useState(isClientSide());
    const csrf = useSelector(selectCsrf);

    useEffect(() => {
        setOnClientSide(isClientSide);
    }, []);

    const [showRequestFormPopup, setShowRequestFormPopup] = useState(false);
    const [salePackageId, setSalePackageId] = useState(0);

    const [popup, setPopup] = useState({
        isResultOpened: false,
        resultTitle: '',
        resultText: '',
        resultType: EResultPopupStatus.SUCCESS,
    });

    const openPopupResult = useCallback((ok: boolean, message: string) => {
        setPopup({
            isResultOpened: true,
            resultType: ok ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
            resultTitle: ok
                ? 'Заявка на подключение отправлена'
                : 'Произошла ошибка',
            resultText: ok ? 'Мы свяжемся с вами в ближайшее время' : message,
        });
        setShowRequestFormPopup(false);
    }, []);

    const submitRequest = useCallback(async(params: ISubmitParams) => {
        const dataParams: RequestDto = {
            fio: params.fio as string,
            phone: params.phone,
            product_id: [0],
            check_call_type: window && window.location.href,
            plan: salePackageId ? +salePackageId : undefined,
            csrf,
        };

        const resp = await doRequest.apiRequest<ResponseDto>(
            requestByConnection(dataParams),
        );

        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            openPopupResult(ok, message);

            return { ok, message, status };
        } else {
            return {
                ok: false,
                message: 'Не удалось отправить заявку.',
                status: ERequestStatus.error,
            };
        }
    }, [salePackageId]);

    const closePopup = useCallback(() => {
        setPopup(state => ({
            ...state,
            isResultOpened: false,
        }));
        setShowRequestFormPopup(false);
    }, []);

    interface tariffSortingKeyAccessors {
        weight: string,
        sortOnInternet: string,
        sortOnInternetLines: string,
        sortOnBundles: string,
    }

    const sortedTariffsByKey = (receivedTariffs: ITariff[], keyToSort: keyof tariffSortingKeyAccessors) => {
        return [...receivedTariffs]
            .sort((a, b) => {
                const currObj = a[keyToSort];
                const nextObj = b[keyToSort];
                if (currObj !== 0 && nextObj === 0) {
                    return 1;
                }
                if (currObj === 0 && nextObj !== 0) {
                    return -1;
                }
                return currObj - nextObj;
            });
    };

    const preparedTariffs = useMemo(() => {
        const result = line ? line.tariffs : baseTariffs;
        switch (sortField) {
          case ESortingKeys.BUNDLES:
            return sortedTariffsByKey(result, ESortingKeys.BUNDLES);
          case ESortingKeys.INTERNET:
            return sortedTariffsByKey(result, ESortingKeys.INTERNET);
          case ESortingKeys.INTERNET_LINES:
            return sortedTariffsByKey(result, ESortingKeys.INTERNET_LINES);
          case ESortingKeys.WEIGHT:
          default:
            return sortedTariffsByKey(result, ESortingKeys.WEIGHT);
        }
      }, [baseTariffs, line, sortField]);

    const renderTariffCards = () => preparedTariffs.map((tariff) => (
        <TariffCard
            key={tariff.productId}
            isAuth={isAuth}
            tariff={tariff}
            openModal={openPopup}
            setSalePackageId={setSalePackageId}
            setShowRequestFormPopup={setShowRequestFormPopup}
        />
    ));

    return (
        <ST.LineContainer
            // @ts-ignore
            id={line ? `${line.id}` : 'tariff-line'}
            className={className}
            data-test='carousel-container'
        >
            {(line && !!preparedTariffs.length) && (
                <>
                    <ST.LineTitle>{line.title}</ST.LineTitle>
                    <ST.LineBody dangerouslySetInnerHTML={{ __html: line.body }} />
                </>
            )}
            {onClientSide
                ? <TariffLineCarousel desktopSlideWidth={304} mobileSlideWidth={288}>
                    {renderTariffCards()}
                </TariffLineCarousel>
                : <ST.NoSwiperContainer>
                    {renderTariffCards()}
                </ST.NoSwiperContainer>
            }
            <PopupCallBack
                isOpen={showRequestFormPopup}
                onClose={() => setShowRequestFormPopup(false)}
                title={'Заявка на подключение'}
                buttonText={'Подключить'}
                submitRequest={(params) => submitRequest(params)}
                option={'popup'}
                category={CATEGORIES.applicationForm}
            />
            <PopupResult
                isOpen={popup.isResultOpened}
                onClose={closePopup}
                title={popup.resultTitle}
                text={popup.resultText}
                type={popup.resultType}
            />
        </ST.LineContainer>
    );
};

export default TariffLine;
